/**
 * ============================================================
 *  MEMORA INTELLIGENCE — Chat com IA sobre dados da empresa
 * ============================================================
 *
 *  ARQUITETURA (Abordagem C — Híbrida):
 *
 *  Pergunta do usuário
 *       ↓
 *  Backend Django (POST /api/chat/intelligence/)
 *       ↓
 *  LangChain Router — classifica a pergunta:
 *       ├── ESTRUTURADA (números, rankings, médias)
 *       │       → Text-to-SQL: Gemini gera SQL → executa no MySQL → formata resposta
 *       │
 *       └── ANALÍTICA (resumos, pontos fortes/fracos, interpretações)
 *               → SQL busca os textos relevantes → Gemini analisa e responde
 *       ↓
 *  Resposta em texto natural enviada ao frontend
 *
 *  TABELAS UTILIZADAS:
 *    - analise_operador_ia   : nota, ponto_forte, ponto_melhoria, palavras_positivas,
 *                              palavras_negativas, operador, objetivo_melhoria
 *    - analise_reunioes_ia   : assunto, nota, interesse_nivel, estagio_venda, resumo,
 *                              cliente_empresa, objecoes, pontos_criticos, proximos_passos,
 *                              tipo_reuniao, data_reuniao, classificacao, oportunidade
 * ============================================================
 */

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/memora_op.css';

const API = process.env.REACT_APP_API_BASE;

const SUGESTOES = [
  { icon: 'fa-chart-line',           texto: 'Qual operador teve a melhor nota média este mês?' },
  { icon: 'fa-exclamation-triangle', texto: 'Quais clientes têm pontos críticos nas reuniões?' },
  { icon: 'fa-thumbs-up',            texto: 'Quais são os principais pontos fortes do time?' },
  { icon: 'fa-ban',                  texto: 'Quais objeções aparecem com mais frequência?' },
  { icon: 'fa-funnel-dollar',        texto: 'Quais clientes estão na fase de negociação?' },
  { icon: 'fa-user-tie',             texto: 'Resuma os pontos de melhoria do operador Eleandro' },
];

export default function MemoraIntelligence() {
  const navigate = useNavigate();

  const [mensagens, setMensagens]     = useState([]);
  const [pergunta, setPergunta]       = useState('');
  const [carregando, setCarregando]   = useState(false);

  const fimChatRef   = useRef(null);
  const inputRef     = useRef(null);
  const textareaRef  = useRef(null);

  useEffect(() => {
    fimChatRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens, carregando]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [pergunta]);

  const enviar = async () => {
    const texto = pergunta.trim();
    if (!texto || carregando) return;

    const token = localStorage.getItem('access');
    const novaId = Date.now();

    setMensagens(prev => [...prev, {
      id: novaId, role: 'user', content: texto, timestamp: new Date(),
    }]);
    setPergunta('');
    setCarregando(true);

    try {
      const res = await axios.post(
        `${API}/api/chat/intelligence/`,
        { pergunta: texto },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMensagens(prev => [...prev, {
        id: novaId + 1, role: 'assistant',
        content: res.data.resposta,
        timestamp: new Date(),
        tipo: res.data.tipo_consulta,
      }]);
    } catch (err) {
      const detalhe = err.response?.data?.erro || err.message || 'Erro desconhecido';
      setMensagens(prev => [...prev, {
        id: novaId + 1, role: 'assistant',
        content: `Não consegui processar sua pergunta. Detalhes: ${detalhe}`,
        timestamp: new Date(),
        tipo: 'erro',
      }]);
    } finally {
      setCarregando(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
  };

  const fmtHora = (d) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const renderMarkdown = (texto) => {
    return texto.split('\n').map((linha, i) => {
      const partes = linha.split(/\*\*(.*?)\*\*/g).map((parte, j) =>
        j % 2 === 1 ? <strong key={j}>{parte}</strong> : parte
      );
      const isBullet = linha.trimStart().startsWith('- ') || linha.trimStart().startsWith('• ');
      if (isBullet) {
        const conteudo = linha.replace(/^\s*[-•]\s*/, '');
        const cf = conteudo.split(/\*\*(.*?)\*\*/g).map((p, j) =>
          j % 2 === 1 ? <strong key={j}>{p}</strong> : p
        );
        return (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
            <span style={{ color: '#00205b', flexShrink: 0, marginTop: 1 }}>•</span>
            <span>{cf}</span>
          </div>
        );
      }
      if (!linha.trim()) return <div key={i} style={{ height: 6 }} />;
      return <div key={i} style={{ marginBottom: 2 }}>{partes}</div>;
    });
  };

  const TipoBadge = ({ tipo }) => {
    if (tipo === 'estruturada') return <span className="badge bg-primary"   style={{ fontSize: '0.62rem' }}>SQL</span>;
    if (tipo === 'analitica')   return <span className="badge bg-success"   style={{ fontSize: '0.62rem' }}>Análise</span>;
    if (tipo === 'erro')        return <span className="badge bg-danger"    style={{ fontSize: '0.62rem' }}>Erro</span>;
    return null;
  };

  const temConversa = mensagens.length > 0 || carregando;

  return (
    <div style={{
      position: 'fixed', top: 0, left: '6rem', right: 0, bottom: 0,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      background: '#f0f2f8',
      fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      zIndex: 1,
    }}>

      {/* ── Barra superior ── */}
      <div style={{
        flexShrink: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1rem 2rem',
        background: '#fff',
        borderBottom: '1px solid #e4e7f0',
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: '#ffffff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(74,108,247,0.35)',
          }}>
            <i className="fa-solid fa-brain header-icon" style={{ fontSize: '1.1rem' }}></i>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="titulo-pagina" style={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.3px' }}>
                Memor.IA
              </span>
              <span style={{
                background: 'linear-gradient(135deg, #fcfcff, #ac1f1f)',
                color: '#fff', fontSize: '0.6rem', fontWeight: 700,
                padding: '2px 9px', borderRadius: 20, letterSpacing: '0.5px',
              }}>BETA</span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#90b0ac', marginTop: 1 }}>
              Intelligence
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {temConversa && (
            <button
              className="btn btn-sm"
              style={{
                borderRadius: 10, fontSize: '0.8rem',
                background: '#f5f6fa', color: '#6c757d',
                border: '1px solid #e4e7f0',
              }}
              onClick={() => setMensagens([])}
            >
              <i className="fas fa-trash-alt me-1"></i>Limpar
            </button>
          )}
          <button
            className="btn btn-sm"
            onClick={() => navigate('/analise-gestor-memora')}
            style={{
              borderRadius: 10, fontSize: '0.8rem',
              background: '#00205b', color: '#fff', border: 'none',
            }}
          >
            <i className="fas fa-arrow-left me-1"></i>Voltar
          </button>
        </div>
      </div>

      {/* ── Área de mensagens ── */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* ── Estado vazio — centralizado vertical e horizontal ── */}
        {!temConversa && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '2rem 1.5rem', textAlign: 'center',
          }}>
            <div style={{ maxWidth: 780, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

              <div style={{
                width: 90, height: 90, borderRadius: '50%',
                background: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 32px rgba(74,108,247,0.35)',
              }}>
                <i className="fa-solid fa-brain" style={{ color: '#e917bb', fontSize: '2.6rem' }}></i>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.6rem' }}>
                <h4 style={{ fontWeight: 800, margin: 0, fontSize: '1.55rem', color: '#1a1d2e', letterSpacing: '-0.5px' }}>
                  Memor.IA Intelligence
                </h4>
                <span style={{
                  background: 'linear-gradient(135deg, #00205b, #003580)',
                  color: '#fff', fontSize: '0.62rem', fontWeight: 700,
                  padding: '3px 10px', borderRadius: 20, letterSpacing: '0.5px',
                }}>BETA</span>
              </div>

              <p style={{ color: '#7a8199', fontSize: '1rem', maxWidth: 480, marginBottom: '2.5rem', lineHeight: 1.6 }}>
                Pergunte qualquer coisa sobre reuniões e operadores da sua empresa. Obtenha respostas em tempo real
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, width: '100%' }}>
                {SUGESTOES.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setPergunta(s.texto); inputRef.current?.focus(); }}
                    style={{
                      background: '#fff', border: '1.5px solid #e4e7f0',
                      borderRadius: 16, padding: '1.1rem 1.25rem',
                      fontSize: '0.93rem', color: '#3a3d52',
                      textAlign: 'left', cursor: 'pointer',
                      transition: 'border-color .2s, box-shadow .2s, transform .15s',
                      display: 'flex', alignItems: 'flex-start', gap: 12, lineHeight: 1.5,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = '#4a6cf7';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(74,108,247,0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = '#e4e7f0';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: 'rgba(74,108,247,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className={`fas ${s.icon}`} style={{ color: '#4a6cf7', fontSize: '0.95rem' }}></i>
                    </div>
                    <span style={{ paddingTop: 2 }}>{s.texto}</span>
                  </button>
                ))}
              </div>

            </div>
          </div>
        )}

        {/* ── Mensagens ── */}
        {temConversa && (
          <div style={{ padding: '1.5rem' }}>
            {mensagens.map((msg) => (
              <div
                key={msg.id}
                className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                style={{ maxWidth: 900, margin: '0 auto 1rem' }}
              >
                {msg.role === 'assistant' && (
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: '#ffffff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginRight: 12, alignSelf: 'flex-end',
                    boxShadow: '0 2px 8px rgba(74,108,247,0.3)',
                  }}>
                    <i className="fa-solid fa-brain" style={{ color: '#ec147d', fontSize: '0.85rem' }}></i>
                  </div>
                )}

                <div style={{ maxWidth: '74%' }}>
                  <div style={{
                    padding: '0.85rem 1.15rem',
                    borderRadius: msg.role === 'user' ? '20px 20px 5px 20px' : '20px 20px 20px 5px',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #f01494, #003580)'
                      : msg.tipo === 'erro' ? '#fff3cd' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#2d3047',
                    boxShadow: msg.role === 'user'
                      ? '0 4px 14px rgba(0,32,91,0.35)'
                      : '0 2px 8px rgba(0,0,0,0.07)',
                    fontSize: '0.95rem', fontWeight: 400,
                    lineHeight: 1.75, letterSpacing: '-0.01em',
                  }}>
                    {msg.role === 'user' ? msg.content : renderMarkdown(msg.content)}
                  </div>
                  <div
                    className="d-flex align-items-center gap-2 mt-1"
                    style={{ justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}
                  >
                    <small className="text-muted" style={{ fontSize: '0.68rem' }}>{fmtHora(msg.timestamp)}</small>
                    <TipoBadge tipo={msg.tipo} />
                  </div>
                </div>

                {msg.role === 'user' && (
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: '#00205b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, marginLeft: 12, alignSelf: 'flex-end',
                    boxShadow: '0 2px 8px rgba(0,32,91,0.25)',
                  }}>
                    <i className="fas fa-user" style={{ color: '#fff', fontSize: '0.85rem' }}></i>
                  </div>
                )}
              </div>
            ))}

            {carregando && (
              <div style={{ maxWidth: 900, margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: '#fff', boxShadow: '0 2px 8px rgba(74,108,247,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="fa-solid fa-brain" style={{ color: '#e917bb', fontSize: '0.75rem' }}></i>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{
                      width: 7, height: 7, borderRadius: '50%', background: '#4a6cf7',
                      animation: `memoraTyping 1.2s ease-in-out ${i*0.2}s infinite`,
                    }}/>
                  ))}
                </div>
              </div>
            )}

            <div ref={fimChatRef} />
          </div>
        )}

      </div>

      {/* ── Input — maior e moderno ── */}
      <div style={{
        flexShrink: 0,
        padding: '1rem 2rem 1.5rem',
        background: '#f0f2f8',
      }}>
        <div style={{
          maxWidth: 900, margin: '0 auto',
          background: '#fff',
          border: '1.5px solid #e4e7f0',
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          padding: '0.85rem 1rem 0.85rem 1.25rem',
        }}>
          {/* Linha do textarea + botão */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
            {/* Avatar */}
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: '#00205b',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginBottom: 2,
              boxShadow: '0 2px 8px rgba(0,32,91,0.2)',
            }}>
              <i className="fas fa-user" style={{ color: '#fff', fontSize: '0.9rem' }}></i>
            </div>

            {/* Textarea */}
            <textarea
              ref={el => { inputRef.current = el; textareaRef.current = el; }}
              placeholder="Faça uma pergunta sobre reuniões, operadores ou clientes…"
              value={pergunta}
              onChange={e => setPergunta(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={carregando}
              style={{
                flex: 1, border: 'none', outline: 'none',
                resize: 'none', background: 'transparent',
                fontSize: '0.97rem', lineHeight: 1.65,
                maxHeight: 160, overflowY: 'auto',
                padding: '0.3rem 0',
                color: '#111827',
                fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                fontWeight: 400,
                letterSpacing: '-0.01em',
              }}
            />

            {/* Botão enviar */}
            <button
              onClick={enviar}
              disabled={!pergunta.trim() || carregando}
              style={{
                width: 44, height: 44, borderRadius: '50%',
                border: 'none', flexShrink: 0, marginBottom: 1,
                background: pergunta.trim() && !carregando
                  ? 'linear-gradient(135deg, #00205b, #003580)'
                  : '#eef0f8',
                color: pergunta.trim() && !carregando ? '#fff' : '#b0b7cc',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: pergunta.trim() && !carregando ? 'pointer' : 'default',
                transition: 'all .2s',
                boxShadow: pergunta.trim() && !carregando
                  ? '0 4px 14px rgba(0,32,91,0.4)'
                  : 'none',
              }}
            >
              {carregando
                ? <i className="fas fa-spinner fa-spin" style={{ fontSize: '0.95rem' }}></i>
                : <i className="fas fa-paper-plane" style={{ fontSize: '0.95rem' }}></i>
              }
            </button>
          </div>

          {/* Rodapé do input */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: '0.6rem', paddingTop: '0.6rem',
            borderTop: '1px solid #f0f2f8',
          }}>
            <small style={{ color: '#b0b7cc', fontSize: '0.72rem' }}>
              Enter para enviar · Shift+Enter para nova linha
            </small>
            <small style={{ color: '#b0b7cc', fontSize: '0.72rem' }}>
              <i className="fas fa-database me-1" style={{ color: '#4a6cf7' }}></i>
              Tempo real 
            </small>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        @keyframes memoraTyping {
          0%, 60%, 100% { transform: translateY(0);   opacity: 0.4; }
          30%            { transform: translateY(-8px); opacity: 1;   }
        }

        /* Placeholder com cor suave, igual ao ChatGPT */
        textarea::placeholder {
          color: #9ca3af;
          font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
          font-size: 0.97rem;
          font-weight: 400;
        }

        /* Scrollbar fina e discreta nas mensagens */
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
      `}</style>
    </div>
  );
}
